import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * AddItemPopup - A small popup with Mission and Decision icon options
 * Rendered via portal to avoid overflow clipping, centers below button
 * 
 * @param {string} swimlaneLabel - Optional label to show which swimlane the item will be added to
 */
const AddItemPopup = ({ isOpen, onClose, onAddMission, onAddDecision, onAddWait, buttonRef, swimlaneLabel }) => {
  const popupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Position popup below button, centered
  useEffect(() => {
    if (!isOpen || !buttonRef?.current) return;
    
    const updatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;
      
      const buttonRect = button.getBoundingClientRect();
      const popupWidth = 320;
      const viewportWidth = window.innerWidth;
      
      // Center below button
      let left = buttonRect.left + buttonRect.width / 2 - popupWidth / 2;
      const top = buttonRect.bottom + 8;
      
      // Adjust if hitting right edge
      if (left + popupWidth > viewportWidth - 10) {
        left = viewportWidth - popupWidth - 10;
      }
      // Adjust if hitting left edge
      if (left < 10) {
        left = 10;
      }
      
      setPosition({ top, left });
    };
    
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, buttonRef]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) && 
          buttonRef?.current && !buttonRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      ref={popupRef} 
      className="kanban-column__add-popup" 
      style={{ 
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'none'
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Swimlane context header */}
      {swimlaneLabel && (
        <div className="kanban-column__add-popup-context">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>to "{swimlaneLabel}"</span>
        </div>
      )}
      {/* Items row */}
      <div className="kanban-column__add-popup-items">
        <button 
          className="kanban-column__add-popup-item"
          onClick={(e) => {
            e.stopPropagation();
            onAddMission();
            onClose();
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            {/* Exclamation mark - tapered body */}
            <path d="M12 3 L14.5 15 L12 16 L9.5 15 Z" fill="currentColor" />
            {/* Exclamation mark dot - diamond shape */}
            <path d="M12 18 L14 20 L12 22 L10 20 Z" fill="currentColor" />
          </svg>
          <span>Mission</span>
        </button>
        <button 
          className="kanban-column__add-popup-item kanban-column__add-popup-item--decision"
          onClick={(e) => {
            e.stopPropagation();
            onAddDecision();
            onClose();
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2 L22 12 L12 22 L2 12 Z" />
          </svg>
          <span>Decision</span>
        </button>
        <button
          className="kanban-column__add-popup-item"
          onClick={(e) => {
            e.stopPropagation();
            onAddWait?.();
            onClose();
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2h12" />
            <path d="M6 22h12" />
            <path d="M7 2c0 5 5 7 5 10s-5 5-5 10" />
            <path d="M17 2c0 5-5 7-5 10s5 5 5 10" />
          </svg>
          <span>Wait for</span>
        </button>
      </div>
    </div>,
    document.body
  );
};

export default AddItemPopup;

